import { MapUtils } from '../../utility/map-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import {
  cloneData, isSameData, isSameList,
} from '../dto/data-model-utils.js';
import { EventInfo } from './event-info.js';
import { VersionRange } from '../core/version-range.js';
import { ArtistInfo } from './artist-info.js';
import { BrandInfo } from './brand-info.js';
import { OrganizerInfo } from './organizer-info.js';
import { JSONUtils } from '../../utility/json-utils.js';

export class HomePageInfo extends DataModel {
  static readonly TYPE_ID: string = '1a46488b26394556b8694cbfc5a13294';

  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly events: EventInfo[],
    public readonly artists: ArtistInfo[],
    public readonly brands: BrandInfo[],
    public readonly organizers: OrganizerInfo[],
  ) {
    super();
  }

  get $typeId(): string {
    return HomePageInfo.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, HomePageInfo.SCHEMA_VERSION);
  }

  static empty() {
    return new HomePageInfo(
        [],
        [],
        [],
        [],
    );
  }

  static create(args: {
    events: EventInfo[],
    artists: ArtistInfo[],
    brands: BrandInfo[],
    organizers: OrganizerInfo[],
  }) {
    return new HomePageInfo(
        args.events,
        args.artists,
        args.brands,
        args.organizers,
    );
  }

  static fromMap(map: SerializedData, context: SerializationContext): HomePageInfo {
    return new HomePageInfo(
        MapUtils.getList<EventInfo>(map, 'events', (x) => EventInfo.fromMap(context, JSONUtils.decodeRecord(x))),
        MapUtils.getList<ArtistInfo>(map, 'artists', (x) => ArtistInfo.fromMap(context, JSONUtils.decodeRecord(x))),
        MapUtils.getList<BrandInfo>(map, 'brands', (x) => BrandInfo.fromMap(context, JSONUtils.decodeRecord(x))),
        MapUtils.getList<OrganizerInfo>(map, 'organizers', (x) => OrganizerInfo.fromMap(context, JSONUtils.decodeRecord(x))),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      events: this.events.map((x) => x.toMap(context)),
      artists: this.artists.map((x) => x.toMap(context)),
      brands: this.brands.map((x) => x.toMap(context)),
      organizers: this.organizers.map((x) => x.toMap(context)),
    };
  }

  clone(): this {
    return new HomePageInfo(
        this.events.map((x) => cloneData(x)),
        this.artists.map((x) => cloneData(x)),
        this.brands.map((x) => cloneData(x)),
        this.organizers.map((x) => cloneData(x)),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.events, this.events, (a, b) => isSameData(a, b)) &&
      isSameList(other.artists, this.artists, (a, b) => isSameData(a, b)) &&
      isSameList(other.brands, this.brands, (a, b) => isSameData(a, b)) &&
      isSameList(other.organizers, this.organizers, (a, b) => isSameData(a, b));
  }
}

export class HomePageInfoBuilder extends DataModelBuilder<HomePageInfo> {
  build(context: SerializationContext, map: SerializedData): HomePageInfo {
    return HomePageInfo.fromMap(map, context);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(HomePageInfo.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(HomePageInfo.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     * - add `artists` field of type {@link ArtistInfo[]},
     * - add `brands` field of type {@link BrandInfo[]},
     * - add `organizers` field of type {@link OrganizerInfo[]}.
     */
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'artists', []);
      MapUtils.setIfNullOrUndefined(map, 'brands', []);
      MapUtils.setIfNullOrUndefined(map, 'organizers', []);

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
